import { useEffect, useState } from 'preact/hooks';
import type { Socket } from 'socket.io-client';
import { onSocketReady } from '../lib/socket';

/**
 * A realtime nudge delivered on the user's personal Socket.IO room. Mirrors the
 * backend `nudge` event shape emitted by NudgeDeliveryService.emitSocketEvent:
 * `{ nudgeType, title, ...payload }` where the payload carries the deep-link
 * `type` + `threadId` and a human `message`.
 */
export interface NudgeEvent {
  nudgeType: string;
  title: string;
  message?: string;
  threadId?: string;
  threadTitle?: string;
  /** Deep-link kind, e.g. 'thread'. */
  type?: string;
}

function normalizeNudge(payload: unknown): NudgeEvent | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  const title = typeof p.title === 'string' ? p.title : null;
  const nudgeType = typeof p.nudgeType === 'string' ? p.nudgeType : null;
  if (!title || !nudgeType) return null;
  return {
    nudgeType,
    title,
    message: typeof p.message === 'string' ? p.message : undefined,
    threadId: typeof p.threadId === 'string' ? p.threadId : undefined,
    threadTitle: typeof p.threadTitle === 'string' ? p.threadTitle : undefined,
    type: typeof p.type === 'string' ? p.type : undefined,
  };
}

/**
 * Subscribe to realtime `nudge` events on the authenticated user's room and
 * expose the latest one. Only an authenticated (JWT) socket joins `user:<id>`,
 * so a guest connection simply never receives these — the hook is inert. Each
 * received event is a fresh object reference so a consumer effect keyed on it
 * re-fires even for a repeated identical nudge.
 */
export function useUserEvents(): NudgeEvent | null {
  const [latest, setLatest] = useState<NudgeEvent | null>(null);

  useEffect(() => {
    let cleanedUp = false;
    let attached: Socket | null = null;

    const handleNudge = (payload: unknown) => {
      if (cleanedUp) return;
      const evt = normalizeNudge(payload);
      if (evt) setLatest(evt);
    };

    // onSocketReady is a persistent subscription: it fires for the current
    // socket (if any) and again for every future instance, so the listener
    // survives socket recreation (auth change) — Socket.IO does not carry
    // listeners across a fresh connection. Track the attached instance so we
    // never double-register on it and always detach from the right one.
    const cancelReady = onSocketReady((socket) => {
      if (cleanedUp || attached === socket) return;
      attached?.off('nudge', handleNudge);
      socket.on('nudge', handleNudge);
      attached = socket;
    });

    return () => {
      cleanedUp = true;
      cancelReady();
      attached?.off('nudge', handleNudge);
    };
  }, []);

  return latest;
}
